/**
 * Legacy `HandleDeferredClick(button, handlerName)` utility.
 *
 * The 1:1-ported `landingCoursesSection.cshtml` carries the legacy attribute
 * pattern on its certification + recertification buttons:
 *
 *   <button data-href="/acls-certification"
 *           onclick="HandleDeferredClick(this, 'Amri.Home.GetCertifiedHandler')">
 *
 * `HandleDeferredClick` dispatches to the named handler under `Amri.*`. In
 * legacy this was an optimization: defer wiring of feature handlers until
 * after first paint, then route clicks via the namespace.
 *
 * Phase 3.8 ports the utility but most handlers it dispatches to (e.g.
 * `Amri.Home.GetCertifiedHandler` which `$.post`s to a `/check-certified`
 * URL) require server endpoints that don't exist in v2 yet (checkout port
 * is its own track). The utility is wired so clicks don't throw; resolved
 * handlers either run or no-op gracefully if the registration is missing.
 */

import { Amri } from '../amri';

type HandlerFn = (button: HTMLElement) => void;

/** Resolve `'Amri.Home.GetCertifiedHandler'` → the function on the Amri namespace. */
function resolveHandler(path: string): HandlerFn | null {
    const segments = path.split('.');
    if (segments[0] !== 'Amri') {
        return null;
    }
    let current: unknown = Amri;
    for (const segment of segments.slice(1)) {
        if (current && typeof current === 'object' && segment in current) {
            current = (current as Record<string, unknown>)[segment];
        } else {
            return null;
        }
    }
    return typeof current === 'function' ? (current as HandlerFn) : null;
}

/**
 * Global function the Razor `onclick=` attribute calls. Attached to `window`
 * by `initDeferredClick()`.
 */
function handleDeferredClick(button: HTMLElement, handlerPath: string): void {
    const handler = resolveHandler(handlerPath);
    if (handler) {
        handler(button);
        return;
    }
    // No handler registered yet — log + no-op. Common during the port phase
    // when the markup references a handler whose feature hasn't shipped yet.
    if (typeof console !== 'undefined' && typeof console.info === 'function') {
        console.info(`[Amri] HandleDeferredClick: '${handlerPath}' not registered yet (feature pending port)`);
    }
}

export function initDeferredClick(): void {
    (window as unknown as { HandleDeferredClick: typeof handleDeferredClick }).HandleDeferredClick = handleDeferredClick;
}
